/**
 * MigrationPage.js — storage meter + a read-only preview of the old localStorage data.
 *
 * The one-time localStorage → Supabase migration used to live here as a "Run
 * Migration" button that force-upserted every table (and the credentials/theme
 * config) straight over the live data, bypassing every merge. It was removed on
 * 2026-10-01 under the owner's rule that EVERY write is atomic, no exceptions: one
 * click from a browser still holding old local data would have reverted the app.
 * The migration was finished long ago; do not bring a blind writer back.
 */

import React, { useState, useMemo } from 'react';

const LOCAL_KEY = 'strategists_v1';



function readLocal() {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function MigrationPage({ store }) {
  const [preview, setPreview] = useState(null);

  // ── Live storage meter — measures the actual in-memory store (Supabase) ──
  const storageMeter = useMemo(() => {
    if (!store) return null;
    try {
      const kb = obj => (JSON.stringify(obj || {}).length * 2) / 1024; // UTF-16 byte estimate
      const breakdown = [
        { label:'Invoices',      size:kb(store.invoices) },
        { label:'HR data',       size:kb(store.hr) },
        { label:'Reports',       size:kb(store.reports) },
        { label:'Tasks',         size:kb(store.tasks) },
        { label:'Clients',       size:kb(store.clients) + kb(store.parentClients) },
        { label:'Finance',       size:kb(store.oneTimeRevenue) + kb(store.oneTimeExpenses) + kb(store.salaryPayments) + kb(store.subscriptions) + kb(store.commissionPayments) },
        { label:'Employees',     size:kb(store.employees) + kb(store.employeeCredentials) },
        { label:'Leave & OT',    size:kb(store.leaveRequests) + kb(store.overtimeRequests) },
        { label:'Notifications', size:kb(store.notifications) },
        { label:'Updates & Holidays', size:kb(store.updates) + kb(store.holidays) },
      ].filter(x => x.size > 0).sort((a, b) => b.size - a.size);
      const totalKb = breakdown.reduce((s, x) => s + x.size, 0);
      const mb      = totalKb / 1024;
      const limit   = 500;
      const pctUsed = (mb / limit) * 100;
      const rowCount = ['clients','invoices','employees','parentClients','tasks','salaryPayments',
        'oneTimeRevenue','oneTimeExpenses','subscriptions','commissionPayments','leaveRequests',
        'overtimeRequests','reports','holidays','updates','notifications']
        .reduce((s,k) => s + (Array.isArray(store[k]) ? store[k].length : 0), 0);
      return { mb:mb.toFixed(2), pctUsed:pctUsed.toFixed(1), limit, breakdown, rowCount };
    } catch { return null; }
  }, [store]);


  function handlePreview() {
    const s = readLocal();
    if (!s) { setPreview({ error: true }); return; }
    setPreview({
      '📋 Clients':             (s.clients||[]).length,
      '🧾 Invoices':            (s.invoices||[]).length,
      '👥 Employees':           (s.employees||[]).length,
      '🏢 Parent clients':      (s.parentClients||[]).length,
      '✅ Tasks':               (s.tasks||[]).length,
      '💰 Salary payments':     (s.salaryPayments||[]).length,
      '📈 One-time revenue':    (s.oneTimeRevenue||[]).length,
      '📉 One-time expenses':   (s.oneTimeExpenses||[]).length,
      '🔄 Subscriptions':       (s.subscriptions||[]).length,
      '💸 Commission payments': (s.commissionPayments||[]).length,
      '🏖  Leave requests':     (s.leaveRequests||[]).length,
      '⏰ Overtime requests':   (s.overtimeRequests||[]).length,
      '📊 Reports':             (s.reports||[]).length,
      '📅 Holidays':            (s.holidays||[]).length,
      '📣 Updates':             (s.updates||[]).length,
      '🗂  HR records':         Object.keys(s.hr||{}).length,
      '🔑 Credentials':        Object.keys(s.employeeCredentials||{}).length,
    });
  }


  const card = { background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'1.25rem', marginBottom:16 };
  const row  = { display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid var(--border)', fontSize:13 };

  return (
    <div style={{ maxWidth:640, margin:'0 auto', padding:'2rem 1rem', display:'flex', flexDirection:'column', gap:0 }}>

      {/* Header */}
      <div style={card}>
        <div style={{ fontFamily:'var(--serif)', fontWeight:400, fontSize:16, color:'var(--text)', marginBottom:6 }}>
          Data Migration Tool
        </div>
        <div style={{ fontSize:13, color:'var(--text3)', lineHeight:1.7 }}>
          Uploads <strong style={{color:'var(--text)'}}>all</strong> your localStorage data
          directly to Supabase, bypassing any cache. Run this on{' '}
          <code style={{background:'var(--bg4)',padding:'1px 6px',borderRadius:'var(--radius)'}}>localhost:3000</code>{' '}
          while logged in as admin. Safe to run multiple times.
        </div>
      </div>

      {/* Live storage / data usage meter */}
      {storageMeter && (
        <div style={card}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, gap:12, flexWrap:'wrap' }}>
            <div>
              <div style={{ fontFamily:'var(--serif)', fontWeight:400, fontSize:16, color:'var(--text)', marginBottom:2 }}>App data usage</div>
              <div style={{ fontSize:13, color:'var(--text3)', fontFamily:'var(--mono)', fontVariantNumeric:'lining-nums tabular-nums' }}>{storageMeter.mb} MB used of {storageMeter.limit} MB · {storageMeter.rowCount.toLocaleString()} records</div>
            </div>
            <div style={{ fontSize:24, fontFamily:'var(--mono)', fontWeight:800, fontVariantNumeric:'lining-nums tabular-nums', color:Number(storageMeter.pctUsed)>80?'var(--red)':Number(storageMeter.pctUsed)>50?'var(--orange)':'var(--green)' }}>
              {storageMeter.pctUsed}%
            </div>
          </div>
          <div style={{ height:8, borderRadius:'var(--radius)', background:'var(--bg4)', overflow:'hidden', marginBottom:16 }}>
            <div style={{ height:'100%', borderRadius:'var(--radius)', transition:'width 0.4s ease',
              width:Math.min(Number(storageMeter.pctUsed),100)+'%',
              background:Number(storageMeter.pctUsed)>80?'var(--red)':Number(storageMeter.pctUsed)>50?'var(--orange)':'var(--green)' }}/>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(min(100%,130px),1fr))', gap:8 }}>
            {storageMeter.breakdown.map(item => (
              <div key={item.label} style={{ background:'var(--bg3)', borderRadius:'var(--radius)', padding:'8px 12px' }}>
                <div style={{ fontSize:10, fontFamily:'var(--mono)', fontWeight:400, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:4 }}>{item.label}</div>
                <div style={{ fontSize:13, fontWeight:600, color:'var(--text)', fontFamily:'var(--mono)', fontVariantNumeric:'lining-nums tabular-nums' }}>
                  {item.size<1 ? (item.size*1024).toFixed(0)+' B' : item.size<1024 ? item.size.toFixed(1)+' KB' : (item.size/1024).toFixed(2)+' MB'}
                </div>
              </div>
            ))}
          </div>
          {Number(storageMeter.pctUsed)>70 && (
            <div style={{ marginTop:12, padding:'8px 12px', background:'rgba(199,154,91,0.1)', border:'1px solid rgba(199,154,91,0.25)', borderRadius:'var(--radius)', fontSize:12, color:'var(--orange)' }}>
              ⚠️ Data usage over 70% — purge old reports from the Reports page to free up space.
            </div>
          )}
        </div>
      )}

      {/* Buttons */}
      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <button onClick={handlePreview} 
          style={{ padding:'8px 18px', fontSize:13, fontWeight:600, borderRadius:'var(--radius)',
            border:'1px solid var(--border2)', background:'var(--bg3)',
            color:'var(--text)', cursor:'pointer' }}>
          Preview local data
        </button>
      </div>

      {/* Preview */}
      {preview && (
        <div style={card}>
          <div style={{ fontWeight:600, fontSize:14, color:'var(--text)', marginBottom:10 }}>
            {preview.error ? '❌ No data found in localStorage' : '📦 Data in localStorage'}
          </div>
          {!preview.error && Object.entries(preview).map(([k,v]) => (
            <div key={k} style={row}>
              <span style={{color:'var(--text3)'}}>{k}</span>
              <strong style={{color: v>0?'var(--text)':'var(--text3)', fontFamily:'var(--mono)', fontVariantNumeric:'lining-nums tabular-nums'}}>{v}</strong>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
